import './Checkbox.css';
import React, { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import GlobalProps from '../../globalProps';
import { useForm } from '../Form';

interface CheckboxBase extends GlobalProps {
  label?: React.ReactNode;
  required?: boolean;
  forceSpacing?: boolean;
  readOnly?: boolean;
}

interface CheckboxProp extends CheckboxBase {
  name: string;
  errorMessage?: string;
}

export function Checkbox({
  name,
  label,
  required,
  readOnly,
  errorMessage,
  className,
  style,
  forceSpacing,
}: CheckboxProp): JSX.Element {
  const [value, setValue, triedToSubmit, reportValidity] = useForm<boolean>(name);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    return () => {
      reportValidity(true);
    };
  }, []);

  const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);

  const validationMessage =
    errorMessage || ((triedToSubmit || touched) && inputRef.current && inputRef.current.validationMessage) || undefined;

  return (
    <div
      className={classnames('check-box__container', { 'check-box__container--force-spacing': forceSpacing }, className)}
      style={style}
    >
      <input
        type="checkbox"
        className="check-box__input"
        name={name}
        checked={value || false}
        onChange={(e) => {
          if (!readOnly) {
            setValue(e.target.checked);
            setTouched(true);
          }
        }}
        required={required}
        ref={inputRef}
      />
      {label && (
        <div className="check-box__label">
          <span
            className={classnames('check-box__label-text', { 'check-box__label-text--readonly': readOnly })}
            onClick={() => {
              if (!readOnly) {
                setValue(!value);
              }
            }}
          >
            {label}
          </span>
          {validationMessage && <img className="check-box_label__alert" src="/icons/alert-circle.svg" alt="" />}
        </div>
      )}
      {validationMessage && <p className="check-box__error">{validationMessage}</p>}
    </div>
  );
}

interface SimpleCheckboxProp extends CheckboxBase {
  value?: boolean;
  onChange?: () => void;
  partiallyChecked?: boolean;
}

export function SimpleCheckbox({
  value,
  label,
  required,
  className,
  style,
  forceSpacing,
  onChange,
  readOnly,
  partiallyChecked,
}: SimpleCheckboxProp): JSX.Element {
  return (
    <div
      className={classnames('check-box__container', { 'check-box__container--force-spacing': forceSpacing }, className)}
      style={style}
    >
      <input
        type="checkbox"
        className={classnames('check-box__input', {
          'check-box__input--disabled': readOnly,
          'check-box__input--partial': partiallyChecked,
        })}
        checked={value || false}
        onChange={onChange}
        required={required}
        readOnly={readOnly}
      />
      {label && (
        <p className="check-box__label">
          <span className="check-box__label-text" onClick={onChange}>
            {label}
          </span>
        </p>
      )}
    </div>
  );
}
